import { useGetUnitList } from '@shared/api';
import { IngredientPickerItem } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import InputFormField from '@shared/components/form/input-form-field';
import { RepeatableRowComponentProps } from '@shared/components/form/types';
import IngredientPicker from '@shared/components/pickers/ingredient-picker';
import { UnitPicker } from '@shared/components/product-form/components/unit-picker';
import { Button } from '@shared/components/ui/button';
import { FormLabel } from '@shared/components/ui/form';
import { Skeleton } from '@shared/components/ui/skeleton';
import { cn } from '@shared/lib/utils';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface RecipeBaseFoodRowProps extends RepeatableRowComponentProps {}

export const emptyBaseFoodRow = {
  entityUuid: '',
  entityType: 'base_food' as const,
  quantity: '',
  unitUuid: '',
};

export default function RecipeBaseFoodRow({
  index,
  remove,
  setDisableAdd,
  includeErrorMessage,
}: RecipeBaseFoodRowProps) {
  const {
    data: units,
    isLoading: unitsIsLoading,
    error: unitsError,
  } = useGetUnitList();

  const { getValues, watch, setValue } = useFormContext();

  const watchIngredientUuid = watch(`ingredients.${index}.entityUuid`);
  const [selectedIngredient, setSelectedIngredient] =
    useState<IngredientPickerItem | null>(null);

  useEffect(() => {
    unitsIsLoading ? setDisableAdd(true) : setDisableAdd(false);
  }, [setDisableAdd, unitsIsLoading]);

  const existingIngredient = getValues(`ingredients.${index}`);
  const formattedSelectedIngredient: IngredientPickerItem | null =
    existingIngredient?.entityUuid
      ? {
          uuid: existingIngredient.entityUuid,
          name: existingIngredient.name,
          type: existingIngredient.entityType,
          availableUnitTypes: existingIngredient.availableUnitTypes,
        }
      : null;

  if (unitsIsLoading) {
    return (
      <div className="grid grid-cols-12 items-end gap-2">
        <Skeleton className="col-span-7 h-10 rounded-lg" />
        <Skeleton className="col-span-2 h-10 rounded-lg" />
        <Skeleton className="col-span-2 h-10 rounded-lg" />
        <Skeleton className="col-span-1 h-10 rounded-lg" />
      </div>
    );
  }

  if (!units) {
    throw new Error(
      `Failed to load units for ingredient rows ${unitsError || 'No data'}`
    );
  }

  return (
    <div className="grid grid-cols-12 items-start gap-2">
      <IngredientPicker
        name={`ingredients.${index}.entityUuid`}
        showLabel={index === 0}
        className="col-span-7"
        includeErrorMessage={includeErrorMessage}
        testId={`ingredient-picker-${index}`}
        include={['base_foods']}
        onSelect={(item) => {
          setSelectedIngredient(item);
          setValue(`ingredients.${index}.entityType`, item.type);
        }}
        knownSelectedItem={formattedSelectedIngredient ?? undefined}
      />
      <InputFormField
        name={`ingredients.${index}.quantity`}
        label={index === 0 ? 'Quantity' : ''}
        disabled={!watchIngredientUuid}
        type="number"
        min={0.001}
        step="any"
        className="col-span-2"
        inputClassName="text-right"
      />
      <UnitPicker
        name={`ingredients.${index}.unitUuid`}
        units={units}
        selectedIngredient={
          selectedIngredient ?? formattedSelectedIngredient ?? undefined
        }
        index={index}
        className="col-span-2"
      />
      <div className="col-span-1 grid items-end">
        {index === 0 && (
          <FormLabel className="mb-0 opacity-0">Remove</FormLabel>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn('size-10', {
            'opacity-30': getValues('ingredients').length === 1,
            'mt-2': index === 0,
          })}
          disabled={getValues('ingredients').length === 1}
          onClick={() => remove(index)}
        >
          <Icons.x className="size-4" />
        </Button>
      </div>
    </div>
  );
}
